import { FormEvent, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Head from 'next/head';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { MemriseApiError } from 'src/api';
import {
  authenticateWithApple,
  authenticateWithFacebook,
  authenticateWithGoogle,
  GoogleResponse,
  signInWithEmail,
} from 'src/utils/auth';
import { Button, Header, Input, Label, Page } from 'src/components';
import { AppleAuthButton, FacebookAuthButton, GoogleAuthButton } from 'src/utilityComponents';
import RedirectIfAuth from 'src/utilityComponents/RedirectIfAuth';
import { blurFromAllElements } from 'src/utils/browser';
import { ROUTES } from 'src/constants/routes.const';
import { getProductRedirectPath } from 'src/utils/products';
import { useUserContext } from 'src/context/user';
import useSafePush from 'src/hooks/useSafePush';
import { cleanupWebStoragesOnLogout } from 'src/utils/userWebStorages';
import { makeUrlRelativeOrDashboard } from 'src/utils/location';
import { isReplitUrl } from 'src/utils/replit/isReplitUrl';
import { redirectToReplit } from 'src/utils/replit/redirectToReplit';
import { openPopupForReplit } from 'src/utils/replit/openPopupForReplit';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import * as S from './styles';

const msgs = defineMessages({
  oopsSomethingWentWrong: {
    id: 'something_went_wrong',
  },
  title: {
    id: 'login_page_new_header',
  },
  password: {
    id: 'Password',
  },
  requiredPassword: {
    id: 'login_page_error_no_password',
  },
  emailPlaceholder: {
    id: 'onboarding_signup_email_placeholder',
  },
  forgotPassword: {
    id: 'login_page_forgot_password',
  },
  noAccount: {
    id: 'login_page_no_account',
  },
  or: {
    id: 'login_methods',
  },
  login: {
    id: 'sign_in',
  },
  facebookLogin: {
    id: 'sign_in_facebook',
  },
  appleLogin: {
    id: 'sign_in_apple',
  },
  wrongEmailOrPassword: {
    id: 'login_page_wrong_email_or_password',
  },
  usernameOrEmail: {
    id: 'username_or_email',
  },
  absentUsernameOrEmail: {
    id: 'login_page_username_error',
  },
  cookiesError: {
    id: 'login_page_error_cookies',
  },
  pageHeader: {
    id: 'signin_page_head',
    defaultMessage: 'Log in to your account',
  },
});

const Signin = () => {
  const [isAuthorising, setIsAuthorising] = useState(false);
  const intl = useIntl();
  const router = useRouter();
  const { safePush } = useSafePush();
  const userContext = useUserContext();
  const { lang, next } = router.query;
  const nextStr = Array.isArray(next) ? next[0] : next;

  const schema = yup.object().shape({
    username: yup.string().required(intl.formatMessage(msgs.absentUsernameOrEmail)),
    password: yup.string().required(intl.formatMessage(msgs.requiredPassword)),
    google: yup.string(),
    facebook: yup.string(),
    apple: yup.string(),
    usernameSubmit: yup.string(),
  });

  const { formState, register, handleSubmit, setError, clearErrors } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const handleRedirect = async (existingPopup?: Window | null) => {
    if (nextStr && isReplitUrl(nextStr)) {
      const languagePairId = getLanguagePairId();
      await redirectToReplit({
        replitUrl: nextStr,
        languagePairId,
        existingPopup,
      });
    } else if (nextStr) {
      if (existingPopup) existingPopup.close();
      await safePush(makeUrlRelativeOrDashboard(nextStr));
      userContext.refetchUser();
    } else {
      if (existingPopup) existingPopup.close();
      const path = await getProductRedirectPath(userContext.user?.dateJoined);
      await safePush(path);
      userContext.refetchUser();
    }
  };

  const handleGoogleSignIn = async (googleResponse: GoogleResponse) => {
    setIsAuthorising(true);
    const replitPopup = openPopupForReplit(nextStr);
    try {
      await authenticateWithGoogle(googleResponse);
      await handleRedirect(replitPopup);
    } catch (err) {
      replitPopup?.close();
      setIsAuthorising(false);
      setError('usernameSubmit', {
        message: intl.formatMessage(msgs.oopsSomethingWentWrong),
      });
    }
  };

  const handleFacebookSignIn = () => {
    setIsAuthorising(true);
    const replitPopup = openPopupForReplit(nextStr);

    /* istanbul ignore next */
    if (window.FB?.login) {
      window.FB.login(
        async res => {
          try {
            const isError = res.authResponse === null;
            if (isError) {
              replitPopup?.close();
              setError('usernameSubmit', {
                message: intl.formatMessage(msgs.oopsSomethingWentWrong),
              });
              setIsAuthorising(false);
            } else {
              await authenticateWithFacebook(res.authResponse);
              await handleRedirect(replitPopup);
            }
          } catch (err) {
            replitPopup?.close();
            setError('usernameSubmit', {
              message: intl.formatMessage(msgs.oopsSomethingWentWrong),
            });
            setIsAuthorising(false);
          }
        },
        { scope: 'email' },
      );
    } else {
      replitPopup?.close();
      setError('facebook', {
        message: intl.formatMessage(msgs.cookiesError),
      });
      setIsAuthorising(false);
    }
  };

  /* istanbul ignore next */
  const handleAppleSignIn = async () => {
    setIsAuthorising(true);
    const replitPopup = openPopupForReplit(nextStr);

    if (window.AppleID?.auth) {
      try {
        const data = await window.AppleID.auth.signIn();

        try {
          const isError = data.authorization === null;
          if (isError) {
            replitPopup?.close();
            setError('usernameSubmit', {
              message: intl.formatMessage(msgs.oopsSomethingWentWrong),
            });
            setIsAuthorising(false);
          } else {
            await authenticateWithApple(data.authorization);
            await handleRedirect(replitPopup);
          }
        } catch (err) {
          replitPopup?.close();
          setError('usernameSubmit', {
            message: intl.formatMessage(msgs.oopsSomethingWentWrong),
          });
          setIsAuthorising(false);
        }
      } catch (error) {
        replitPopup?.close();
        setError('usernameSubmit', {
          message: intl.formatMessage(msgs.oopsSomethingWentWrong),
        });
        setIsAuthorising(false);
      }
    } else {
      replitPopup?.close();
      setError('apple', {
        message: intl.formatMessage(msgs.cookiesError),
      });
      setIsAuthorising(false);
    }
  };

  const handleEmailSignIn = async (signInDetails: { username: string; password: string }) => {
    setIsAuthorising(true);
    const replitPopup = openPopupForReplit(nextStr);

    try {
      await signInWithEmail(signInDetails);
      await handleRedirect(replitPopup);
    } catch (err) {
      replitPopup?.close();
      setIsAuthorising(false);
      if (err instanceof MemriseApiError && err.httpStatusCode === 400) {
        setError('usernameSubmit', {
          message: intl.formatMessage(msgs.wrongEmailOrPassword),
        });
      } else {
        setError('usernameSubmit', {
          message: intl.formatMessage(msgs.oopsSomethingWentWrong),
        });
      }
    }
  };

  const username = register('username');
  const password = register('password');

  const signUpHref = `${ROUTES.bienvenue}${lang ? `/${lang}` : ''}`;

  useEffect(() => {
    if (isAuthorising) {
      cleanupWebStoragesOnLogout();
    }
  }, [isAuthorising]);

  if (userContext.loading) {
    return (
      <S.LoaderWrapper>
        <S.Loader />
      </S.LoaderWrapper>
    );
  }

  return (
    <Page header={!userContext.user ? <Header logoUrl="https://www.memrise.com/" /> : undefined}>
      <S.Root>
        <Head>
          <title>{intl.formatMessage(msgs.pageHeader)} - Memrise</title>
        </Head>
        <RedirectIfAuth slug={nextStr}>
          <>
            <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
            <S.SocialButtonsWrapper>
              <GoogleAuthButton
                onSignUp={handleGoogleSignIn}
                error={errors.google?.message}
                disabled={isAuthorising}
              />
              <FacebookAuthButton
                disabled={isAuthorising}
                error={errors.facebook?.message}
                onSignUp={handleFacebookSignIn}
              >
                {intl.formatMessage(msgs.facebookLogin)}
              </FacebookAuthButton>
              <AppleAuthButton
                disabled={isAuthorising}
                error={errors.apple?.message}
                onSignUp={handleAppleSignIn}
              >
                {intl.formatMessage(msgs.appleLogin)}
              </AppleAuthButton>
            </S.SocialButtonsWrapper>

            <p>{intl.formatMessage(msgs.or)}</p>
            <S.Form
              onSubmit={async (event: FormEvent) => {
                blurFromAllElements();
                event.preventDefault();
                await handleSubmit(handleEmailSignIn)(event);
              }}
              method="post"
            >
              <div>
                <Label htmlFor="username">{intl.formatMessage(msgs.usernameOrEmail)}</Label>
                <Input
                  id="username"
                  data-testid="signinUsernameInput"
                  placeholder={intl.formatMessage(msgs.emailPlaceholder)}
                  error={errors.username?.message}
                  {...username}
                  onChange={async (e: FormEvent) => {
                    await username.onChange(e);
                    clearErrors();
                  }}
                  large
                  disabled={isAuthorising}
                />
              </div>

              <S.InputGroup>
                <Label htmlFor="password">{intl.formatMessage(msgs.password)}:</Label>
                <Input
                  id="password"
                  data-testid="signinPasswordInput"
                  type="password"
                  error={errors.password?.message}
                  {...password}
                  onChange={async (e: FormEvent) => {
                    await password.onChange(e);
                    clearErrors();
                  }}
                  large
                  placeholder="••••••••"
                  disabled={isAuthorising}
                />
                <S.Links href="/password/reset/" $alignSelf="flex-end">
                  {intl.formatMessage(msgs.forgotPassword)}
                </S.Links>
              </S.InputGroup>

              <Button
                disabled={!isValid}
                wide
                type="submit"
                buttonType="primary"
                buttonSize="large"
                error={errors.usernameSubmit?.message}
                loading={isAuthorising}
                data-testid="signinFormSubmit"
              >
                {intl.formatMessage(msgs.login)}
              </Button>

              <Link href={signUpHref} passHref legacyBehavior>
                <S.SignupLink $hasError={!!errors.usernameSubmit?.message}>
                  {intl.formatMessage(msgs.noAccount)}
                </S.SignupLink>
              </Link>
            </S.Form>
          </>
        </RedirectIfAuth>
      </S.Root>
    </Page>
  );
};

export default Signin;
